import type { Monaco } from '@monaco-editor/react'
import type { languages } from 'monaco-editor'

// SQL Keywords
const SQL_KEYWORDS = [
	'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN',
	'IS', 'NULL', 'TRUE', 'FALSE', 'AS', 'ON', 'JOIN', 'LEFT', 'RIGHT', 
	'INNER', 'OUTER', 'FULL', 'CROSS', 'NATURAL', 'USING', 'GROUP', 'BY',
	'HAVING', 'ORDER', 'ASC', 'DESC', 'LIMIT', 'OFFSET', 'UNION', 'ALL',
	'INTERSECT', 'EXCEPT', 'DISTINCT', 'TOP', 'WITH', 'RECURSIVE', 'CASE',
	'WHEN', 'THEN', 'ELSE', 'END', 'CAST', 'CONVERT', 'COALESCE', 'NULLIF',
	'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'TRUNCATE',
	'CREATE', 'ALTER', 'DROP', 'TABLE', 'VIEW', 'INDEX', 'CONSTRAINT',
	'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'UNIQUE', 'CHECK', 'DEFAULT',
	'EXISTS', 'ANY', 'SOME', 'OVER', 'PARTITION', 'ROW_NUMBER', 'RANK',
	'DENSE_RANK', 'NTILE', 'LAG', 'LEAD', 'FIRST_VALUE', 'LAST_VALUE',
]

// SQL Functions
const SQL_FUNCTIONS = [
	// Aggregate
	{ name: 'COUNT', snippet: 'COUNT(${1:*})', doc: 'Count rows' },
	{ name: 'SUM', snippet: 'SUM(${1:column})', doc: 'Sum of values' },
	{ name: 'AVG', snippet: 'AVG(${1:column})', doc: 'Average of values' },
	{ name: 'MIN', snippet: 'MIN(${1:column})', doc: 'Minimum value' },
	{ name: 'MAX', snippet: 'MAX(${1:column})', doc: 'Maximum value' },
	{ name: 'GROUP_CONCAT', snippet: 'GROUP_CONCAT(${1:column})', doc: 'Concatenate values' },
	// String
	{ name: 'CONCAT', snippet: 'CONCAT(${1:str1}, ${2:str2})', doc: 'Concatenate strings' },
	{ name: 'SUBSTRING', snippet: 'SUBSTRING(${1:str}, ${2:start}, ${3:length})', doc: 'Extract substring' },
	{ name: 'UPPER', snippet: 'UPPER(${1:str})', doc: 'Convert to uppercase' },
	{ name: 'LOWER', snippet: 'LOWER(${1:str})', doc: 'Convert to lowercase' },
	{ name: 'TRIM', snippet: 'TRIM(${1:str})', doc: 'Remove whitespace' },
	{ name: 'LENGTH', snippet: 'LENGTH(${1:str})', doc: 'String length' },
	{ name: 'REPLACE', snippet: 'REPLACE(${1:str}, ${2:old}, ${3:new})', doc: 'Replace substring' },
	{ name: 'COALESCE', snippet: 'COALESCE(${1:val1}, ${2:val2})', doc: 'Return first non-null' },
	{ name: 'NVL', snippet: 'NVL(${1:expr}, ${2:default})', doc: 'Null value replacement' },
	{ name: 'ISNULL', snippet: 'ISNULL(${1:expr}, ${2:default})', doc: 'Null check replacement' },
	// Date
	{ name: 'GETDATE', snippet: 'GETDATE()', doc: 'Current date/time' },
	{ name: 'CURRENT_TIMESTAMP', snippet: 'CURRENT_TIMESTAMP', doc: 'Current timestamp' },
	{ name: 'DATEADD', snippet: 'DATEADD(${1:interval}, ${2:number}, ${3:date})', doc: 'Add to date' },
	{ name: 'DATEDIFF', snippet: 'DATEDIFF(${1:interval}, ${2:start}, ${3:end})', doc: 'Date difference' },
	{ name: 'YEAR', snippet: 'YEAR(${1:date})', doc: 'Extract year' },
	{ name: 'MONTH', snippet: 'MONTH(${1:date})', doc: 'Extract month' },
	{ name: 'DAY', snippet: 'DAY(${1:date})', doc: 'Extract day' },
	{ name: 'FORMAT', snippet: 'FORMAT(${1:date}, ${2:format})', doc: 'Format date' },
	// Numeric
	{ name: 'ROUND', snippet: 'ROUND(${1:number}, ${2:decimals})', doc: 'Round number' },
	{ name: 'FLOOR', snippet: 'FLOOR(${1:number})', doc: 'Round down' },
	{ name: 'CEILING', snippet: 'CEILING(${1:number})', doc: 'Round up' },
	{ name: 'ABS', snippet: 'ABS(${1:number})', doc: 'Absolute value' },
	// Conversion
	{ name: 'CAST', snippet: 'CAST(${1:expr} AS ${2:type})', doc: 'Convert type' },
	{ name: 'CONVERT', snippet: 'CONVERT(${1:type}, ${2:expr})', doc: 'Convert type' },
	// Window
	{ name: 'ROW_NUMBER', snippet: 'ROW_NUMBER() OVER (${1:PARTITION BY col ORDER BY col})', doc: 'Row number' },
	{ name: 'RANK', snippet: 'RANK() OVER (${1:PARTITION BY col ORDER BY col})', doc: 'Rank with gaps' },
	{ name: 'DENSE_RANK', snippet: 'DENSE_RANK() OVER (${1:PARTITION BY col ORDER BY col})', doc: 'Rank without gaps' },
	{ name: 'LAG', snippet: 'LAG(${1:col}, ${2:1}) OVER (ORDER BY ${3:col})', doc: 'Previous row value' },
	{ name: 'LEAD', snippet: 'LEAD(${1:col}, ${2:1}) OVER (ORDER BY ${3:col})', doc: 'Next row value' },
]

// SQL Snippets
const SQL_SNIPPETS = [
	{ 
		name: 'SELECT basic', 
		prefix: 'sel',
		snippet: 'SELECT ${1:*}\nFROM ${2:table}\nWHERE ${3:condition}',
		doc: 'Basic SELECT statement'
	},
	{ 
		name: 'SELECT with JOIN', 
		prefix: 'selj',
		snippet: 'SELECT ${1:columns}\nFROM ${2:table1} t1\nINNER JOIN ${3:table2} t2 ON t1.${4:id} = t2.${5:id}\nWHERE ${6:condition}',
		doc: 'SELECT with JOIN'
	},
	{ 
		name: 'LEFT JOIN', 
		prefix: 'lj',
		snippet: 'LEFT JOIN ${1:table} ${2:alias} ON ${3:condition}',
		doc: 'LEFT JOIN clause'
	},
	{ 
		name: 'INNER JOIN', 
		prefix: 'ij',
		snippet: 'INNER JOIN ${1:table} ${2:alias} ON ${3:condition}',
		doc: 'INNER JOIN clause'
	},
	{ 
		name: 'CTE', 
		prefix: 'cte',
		snippet: 'WITH ${1:cte_name} AS (\n\tSELECT ${2:columns}\n\tFROM ${3:table}\n)\nSELECT * FROM ${1:cte_name}',
		doc: 'Common Table Expression'
	},
	{ 
		name: 'CASE WHEN', 
		prefix: 'case',
		snippet: 'CASE\n\tWHEN ${1:condition} THEN ${2:result}\n\tELSE ${3:default}\nEND',
		doc: 'CASE expression'
	},
	{ 
		name: 'GROUP BY with HAVING', 
		prefix: 'grp',
		snippet: 'GROUP BY ${1:columns}\nHAVING ${2:condition}',
		doc: 'GROUP BY with HAVING'
	},
	{ 
		name: 'INSERT INTO', 
		prefix: 'ins',
		snippet: 'INSERT INTO ${1:table} (${2:columns})\nVALUES (${3:values})',
		doc: 'INSERT statement'
	},
	{ 
		name: 'UPDATE', 
		prefix: 'upd',
		snippet: 'UPDATE ${1:table}\nSET ${2:column} = ${3:value}\nWHERE ${4:condition}',
		doc: 'UPDATE statement'
	},
]

let isRegistered = false

/**
 * Register SQL autocomplete provider for Monaco editor
 * Call this once when the app initializes or when first Monaco editor mounts
 */
export const registerSqlAutocomplete = (monaco: Monaco) => {
	if (isRegistered) return
	isRegistered = true

	monaco.languages.registerCompletionItemProvider('sql', {
		triggerCharacters: [' ', '.', '('],
		provideCompletionItems: (model, position) => {
			const word = model.getWordUntilPosition(position)
			const range = {
				startLineNumber: position.lineNumber,
				endLineNumber: position.lineNumber,
				startColumn: word.startColumn,
				endColumn: word.endColumn,
			}

			const suggestions: languages.CompletionItem[] = []

			// Add keywords
			SQL_KEYWORDS.forEach((keyword) => {
				suggestions.push({
					label: keyword,
					kind: monaco.languages.CompletionItemKind.Keyword,
					insertText: keyword,
					range,
					detail: 'Keyword',
				})
				// Also add lowercase version
				suggestions.push({
					label: keyword.toLowerCase(),
					kind: monaco.languages.CompletionItemKind.Keyword,
					insertText: keyword.toLowerCase(),
					range,
					detail: 'Keyword',
				})
			})

			// Add functions with snippets
			SQL_FUNCTIONS.forEach((fn) => {
				suggestions.push({
					label: fn.name,
					kind: monaco.languages.CompletionItemKind.Function,
					insertText: fn.snippet,
					insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
					range,
					detail: 'Function',
					documentation: fn.doc,
				})
			})

			// Add snippets
			SQL_SNIPPETS.forEach((snippet) => {
				suggestions.push({
					label: snippet.prefix,
					kind: monaco.languages.CompletionItemKind.Snippet,
					insertText: snippet.snippet,
					insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
					range,
					detail: snippet.name,
					documentation: snippet.doc,
				})
			})

			return { suggestions }
		},
	})
}

